import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

export type DeliveryNotesBulkResponse = {
  assets: unknown[];
  nextLink: string;
  updateLink: string;
  totalAssets?: number;
  hasNextPage?: boolean;
  paginatedCount?: number;
};

const zodDeliveryNotesBulkBodySchema = z.object({
  ids: z.array(z.uuid()),
});

/**
 * Fetches delivery notes by their ids in bulk from the API.
 * Uses POST to avoid URL length limits with large ID lists.
 * @param deliveryNoteIds - The IDs of the delivery notes to fetch.
 * @returns The delivery notes data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/query_multiple_delivery_notes_asset_delivery_note_ids_query_post
 */
export const fetchDeliveryNotesBulk = async (deliveryNoteIds: UUID[]) => {
  const body = { ids: deliveryNoteIds };

  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.QUERY_NOTES_BY_IDS(), {
      json: body,
      validate: {
        body: zodDeliveryNotesBulkBodySchema,
      },
    })
    .json<DeliveryNotesBulkResponse>();

  return response;
};

/**
 * React Query based custom hook for getting the data for multiple delivery notes within a given list of deliveryNoteIds.
 * @param deliveryNoteIds - The IDs of the delivery notes to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryDeliveryNotesBulk = (
  deliveryNoteIds: UUID[],
  options: Omit<
    UseQueryOptions<DeliveryNotesBulkResponse>,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery<DeliveryNotesBulkResponse>({
    enabled: Boolean(deliveryNoteIds?.length),
    queryFn: async () => fetchDeliveryNotesBulk(deliveryNoteIds),
    queryKey: queryKeysDeliveryNote.getBulk(deliveryNoteIds),
    ...options,
    refetchOnWindowFocus: false,
  });
};
